import { usePrevious } from '@cosuno/react-hooks';
import { arrow, flip, offset, type Placement, shift } from '@floating-ui/dom';
import {
  safePolygon,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import React, { useState } from 'react';
import { Transition } from 'react-transition-group';

import Portal from '#@/plumbing/Portal';
import {
  CARD_OPENING_TRANSITION_DURATION,
  CARD_OPENING_TRANSITION_DURATION_DELAYED,
} from '#@/shared/constants';
import type { DocumentElement } from '#@/shared/types';
import { isNodeEmpty } from '#@/shared/utils/react';
import { isEnter, isExiting } from '#@/shared/utils/transitionStates';

import useTooltipState from './hooks/useTooltipState';
import {
  ARROW_HEIGHT,
  ARROW_PADDING,
  ARROW_TIP_RADIUS,
  ARROW_WIDTH,
  CardArrow,
  TOOLTIP_EDGE_OFFSET,
  TOOLTIP_OFFSET,
  TooltipCard,
} from './Styles';
import type { ChildrenProps } from './types';

const variantToTimeout = {
  delayed: CARD_OPENING_TRANSITION_DURATION_DELAYED,
  standard: CARD_OPENING_TRANSITION_DURATION,
} as const;

interface RenderContentConfig {
  closeTooltip: () => void;
  updateTooltipPosition: () => void;
}

export interface GenericTooltipProps<TriggerElement> {
  /**
   * Set to true if you want the tooltip width to be based on its content.
   * By default, the width is fixed.
   */
  flexibleWidth?: boolean;
  /**
   * Placement of the tooltip. Default: 'bottom'.
   * For valid values, see https://floating-ui.com/docs/computePosition#placement
   */
  placement?: Placement;
  renderContent(config: RenderContentConfig): React.ReactNode;
  children: (props: ChildrenProps<TriggerElement>) => React.ReactNode;
  /**
   * Whether you want the tooltip to open right away ('standard')
   * or after a short delay ('delayed').
   */
  timingVariant?: 'delayed' | 'standard';
  /** Allows clients to control whether or not the tooltip is open. */
  isOpen?: boolean;
  /** Allows the component to set the open state maintained by the client. */
  setIsOpen?: (value: boolean) => void;
  /**
   * If set to `true`, the tooltip is opened/closed depending on whether the user
   * hovers the cursor over the trigger element. The default is `true`.
   */
  hoverEnabled?: boolean;
  /**
   * If set to `true`, the tooltip is closed when the user clicks somewhere
   * outside it or presses the escape key. The default is `true`.
   */
  dismissEnabled?: boolean;

  /**
   * By default, the tooltip is dismissed when scrolling occurs in any ancestor.
   * Setting this to `false` prevents this behavior.
   */
  dismissOnAncestorScroll?: boolean;

  isTriggerElementClickable: boolean;

  /**
   * By default, some padding is applied to the tooltip content. Passing `false`
   * here removes it, however.
   */
  addPadding?: boolean;

  onOpenStateChange?: (isOpen: boolean) => void;
}

function GenericTooltip<TriggerElement extends DocumentElement>({
  flexibleWidth,
  placement,
  renderContent,
  children,
  timingVariant = 'standard',
  isOpen: isOpenOverride,
  setIsOpen: setIsOpenOverride,
  hoverEnabled = true,
  dismissEnabled = true,
  dismissOnAncestorScroll = true,
  isTriggerElementClickable,
  addPadding = true,
  onOpenStateChange,
}: GenericTooltipProps<TriggerElement>) {
  const [arrowRef, setArrowRef] = useState<SVGSVGElement | null>(null);

  const { isOpen, openTooltip, closeTooltip } = useTooltipState({
    isOpenOverride,
    setIsOpenOverride,
    onOpenStateChange,
  });

  const transitionDelay = variantToTimeout[timingVariant] - CARD_OPENING_TRANSITION_DURATION;

  const { refs, floatingStyles, context } = useFloating({
    open: true,
    onOpenChange: (open) => (open ? openTooltip() : closeTooltip()),
    placement,
    strategy: 'fixed',
    middleware: [
      shift({ padding: TOOLTIP_EDGE_OFFSET }),
      offset(TOOLTIP_OFFSET + ARROW_HEIGHT),
      flip({ padding: TOOLTIP_EDGE_OFFSET }),
      ...(arrowRef ? [arrow({ padding: ARROW_PADDING, element: arrowRef })] : []),
    ],
  });

  const content = renderContent({ closeTooltip, updateTooltipPosition: context.update });
  const previousContent = usePrevious(content);

  const referencePress = isTriggerElementClickable;
  const move = !referencePress;

  const hover = useHover(context, {
    enabled: hoverEnabled,
    move,
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context, {
    enabled: dismissEnabled,
    referencePress,
    bubbles: { escapeKey: true },
    ancestorScroll: dismissOnAncestorScroll,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

  return (
    <>
      {children({ ref: refs.setReference, getReferenceProps })}
      <Transition in={isOpen} timeout={CARD_OPENING_TRANSITION_DURATION} unmountOnExit>
        {(state) => {
          // This maintains the subtle exiting animation in case the tooltip becomes empty before
          // it exits.
          const contentToDisplay =
            isExiting(state) && isNodeEmpty(content) ? previousContent : content;

          return (
            <Portal>
              <TooltipCard
                {...getFloatingProps()}
                ref={refs.setFloating}
                $transitionDelay={transitionDelay}
                $flexibleWidth={flexibleWidth}
                $isOpen={isEnter(state)}
                $addPadding={addPadding}
                onClick={(event) => event.stopPropagation()}
                style={floatingStyles}
                data-cy-tooltip
                data-cy-tooltip-open={isEnter(state)}
              >
                <CardArrow
                  ref={setArrowRef}
                  context={context}
                  width={ARROW_WIDTH}
                  height={ARROW_HEIGHT}
                  tipRadius={ARROW_TIP_RADIUS}
                />
                {contentToDisplay}
              </TooltipCard>
            </Portal>
          );
        }}
      </Transition>
    </>
  );
}

export default GenericTooltip;
